import React, { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { GoogleMap as Map, useJsApiLoader, Marker } from '@react-google-maps/api';
import { Button, Container, Icon } from '@pella/sash-framework';
import { isValidPostal, isValidArray } from '../../../utilities/validations';
import { pellaLatitude, pellaLongitude } from '../../../utilities/constants';
import { usePostalCode } from '../../../../hooks/use-postal-code';
import { useLocation } from '../../../../context/LocationContext';
import LocationList from '../location-list';
import LoadingOverlay from '../../../common/loading-overlay';
import PromoBanner from '../../../common/promo-banner';

const GoogleMap = () => {
	const markerRef = useRef([]);
	const userZip = usePostalCode();
	const locations = useSelector((state) => state.locations);
	const [center, setCenter] = useState({ lat: pellaLatitude, lng: pellaLongitude });
	const [selectedPlace, setSelectedPlace] = useState('');
	const [zip, setZip] = useState({ saved: '', input: '' });
	const [status, setStatus] = useState('idle');
	const { setLocation } = useLocation();

	const { isLoaded } = useJsApiLoader({
		id: 'google-map-script',
		googleMapsApiKey: process.env.GATSBY_GOOGLE_MAPS_API_KEY,
	});

	useEffect(() => setZip({ saved: userZip, input: userZip }), [userZip]);

	// When dispatch for stores gets run, check for updates to locations and update map center/selection
	useEffect(() => {
		if (isValidArray(locations?.showrooms)) {
			onMarkerClick(locations.showrooms[0].line_1);
			setCenter({
				lat: locations.showrooms[0].location.coordinates[1],
				lng: locations.showrooms[0].location.coordinates[0],
			});
			setSelectedPlace(locations.showrooms[0].line_1);
		}
	}, [locations]);

	const onZipIconClick = () => {
		setZip({ ...zip, input: '' });
		setStatus('edit');
		document.querySelector('#mapZip').focus();
	};

	const onInputChange = (event) => {
		setZip({ ...zip, input: event.target.value });
	};

	const onInputFocus = () => {
		setZip({ ...zip, input: '' });
		setStatus('edit');
	};

	const onInputBlur = () => {
		if (!zip.input) {
			setZip({ ...zip, input: zip.saved });
			setStatus('idle');
		}
	};

	const onFormSubmit = useCallback(
		async (event) => {
			if (event) event.preventDefault();

			if (zip.input === zip.saved) {
				setStatus('idle');
				return;
			}

			setStatus('idle');
			setZip({ ...zip, saved: zip.input });
			await setLocation(zip.input, true, event);
		},
		[zip, setLocation]
	);

	const onMarkerClick = (name, marker) => {
		setSelectedPlace(name);

		if (marker && marker != null) {
			setCenter({
				lat: marker.props.position.lat,
				lng: marker.props.position.lng,
			});
		}
	};

	return (
		<div
			className='showroomMap relative pt-8 md:bg-gray-5'
			data-testid='showroom-landing-map'
		>
			<Container>
				{!isLoaded && <LoadingOverlay fixed={false} />}

				<div className='grid auto-rows-min grid-cols-1 gap-x-4 md:grid-cols-2 md:max-xl:grid-rows-[auto_1fr_auto_auto] xl:grid-cols-[373px_1fr_276px] xl:grid-rows-[auto_1fr]'>
					<div className='showroomMap-header col-start-1 row-start-1 mb-2.5 md:mb-4'>
						<div className='flex justify-between'>
							<h3 className='m-0 flex-1'>
								Nearby
								<br />
								Showrooms
							</h3>

							<form
								onSubmit={onFormSubmit}
								className='flex flex-none flex-row items-center justify-center md:flex-none'
								autoComplete='off'
							>
								{status !== 'edit' && (
									<button
										type='button'
										className='rtaShowroomPanel-locationBtn flex-none'
										onClick={onZipIconClick}
										aria-label='Edit Zip Code'
									>
										<Icon
											name='location_on'
											className='block text-gray-50'
										/>
									</button>
								)}

								<input
									type='text'
									inputMode='numeric'
									id='mapZip'
									name='mapZip'
									value={zip.input}
									onFocus={onInputFocus}
									onBlur={onInputBlur}
									onChange={onInputChange}
									maxLength={7} // 7 for Canadian postal codes
									className={`min-w-[5ch] flex-none cursor-pointer border-b-2 border-b-black bg-transparent py-0.5 text-center text-base font-bold leading-tight tracking-wider focus:cursor-text [&+div]:!hidden ${status === 'edit' ? '!w-[7.5ch]' : ''}`}
									style={{ width: `${zip.input.length + 0.5}ch` }}
									aria-label='Zip Code'
									autoComplete='postal-code'
								/>

								{status === 'edit' && (
									<Button
										type='submit'
										color='primary-alt'
										className='ml-2 !w-auto !px-1 !py-0 !text-sm tracking-wider [&+div]:!hidden'
										disabled={!isValidPostal(zip.input)}
									>
										Update
									</Button>
								)}
							</form>
						</div>
					</div>

					<div className='showroomMap-list col-start-1 row-start-3 mb-4 overflow-y-auto md:row-span-3 md:mb-0 md:h-[800px] xl:row-span-1 xl:h-[430px]'>
						<LocationList
							data={locations?.showrooms}
							selectedPlace={selectedPlace}
						/>
					</div>

					<div className='showroomMap-map col-start-1 row-start-2 mb-4 md:col-start-2 md:row-span-2 md:row-start-1 xl:mb-0 xl:pb-8'>
						{isLoaded && (
							<Map
								center={center}
								zoom={8}
								mapContainerClassName='h-[355px] md:h-full'
								options={{
									mapTypeControl: false,
									streetViewControl: false,
									fullscreenControl: false,
								}}
							>
								{isValidArray(locations?.showrooms) &&
									locations.showrooms.map((location, index) => (
										<Marker
											key={index}
											name={location.name}
											ref={(el) => (markerRef.current[index] = el)}
											onClick={() => onMarkerClick(location.line_1, markerRef.current[index])}
											position={{
												lat: location.location.coordinates[1],
												lng: location.location.coordinates[0],
											}}
											zIndex={selectedPlace !== null && selectedPlace === index ? 2 : 1}
											icon={{ url: `https://images.contentstack.io/v3/assets/bltf589e66bcaecd79c/blt5d51af0d3a33260d/pella-logo-black-spot.png?width=${location.line_1 === selectedPlace ? 72 : 48}` }}
										/>
									))}
							</Map>
						)}
					</div>

					<div className='showroomMap-promo col-start-1 row-start-4 md:col-start-2 md:row-span-2 md:row-start-3 md:pb-8 xl:col-start-3 xl:row-start-1'>
						<PromoBanner
							isCollapsible={false}
							type='component'
							bgColor='transparent'
							badgeText='Local Promotions'
							stacked
						/>
					</div>
				</div>
			</Container>
		</div>
	);
};

GoogleMap.propTypes = {
	google: PropTypes.object,
	panToPoint: PropTypes.object,
	type: PropTypes.string,
};

export default GoogleMap;
